/**
 * Application controller: owns the state and wires the geometry, the painter, the renderers,
 * the overlay and the DOM together. Rendering is on demand: a frame is drawn only when
 * something changed or is animating, and nothing runs while the tab is hidden.
 */
import {
  type RotationColouring,
  findRotationColouring,
  identityPerm,
  invertPerm,
  pairPermutations,
} from '../geometry/colouring';
import type { Complex } from '../geometry/complex';
import { IDENTITY, apply, inverse } from '../geometry/mobius';
import { MODELS, type ModelId } from '../geometry/models';
import {
  type TriangleGroup,
  buildTriangleGroup,
  classifyTriangle,
  fold,
} from '../geometry/triangle-group';
import {
  decodeBinary,
  decodeState,
  encodeBinary,
  encodeState,
  fromBase64Url,
  toBase64Url,
} from '../paint/codec';
import { defaultMotif } from '../paint/default-motif';
import { type Box, domainBox, domainScale } from '../paint/domain';
import { ERASER, type Motif, emptyMotif } from '../paint/motif';
import { type Camera, cameraFor, cameraForBox, screenToDisk } from '../render/camera';
import { CPURenderer, premultiply } from '../render/cpu-renderer';
import { GLRenderer } from '../render/gl-renderer';
import { PAINT } from '../render/palette';
import { DEFAULT_PALETTE, type FrameParams, type SceneSettings } from '../render/scene';
import { Measurement, type MeasureKind } from './measure';
import { type Cursor, OverlayRenderer } from './overlay';
import { Painter } from './painter';
import { type AppState, DEFAULT_STATE, Store, brushFromSlider, isMeasureTool } from './state';
import { ViewController } from './view';

type Renderer = { kind: 'webgl'; gl: GLRenderer } | { kind: 'cpu'; cpu: CPURenderer };

const STORAGE_KEY = 'horocycle:v1';
const HINT_KEY = 'horocycle:hint-done';

export interface NoticeSpec {
  id: string;
  kind: 'info' | 'error';
  html: string;
  actions?: { label: string; run: () => void }[];
  dismissible?: boolean;
}

export interface AppCallbacks {
  onState: (s: AppState, prev: AppState) => void;
  onHistory: (canUndo: boolean, canRedo: boolean) => void;
  onGroupInfo: () => void;
  onReadout: () => void;
  onNotice: (n: NoticeSpec | null) => void;
  onToast: (msg: string, kind?: 'info' | 'error') => void;
  onHintDone: () => void;
}

const safeStorage = {
  get(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      /* storage unavailable (private mode): the app still works */
    }
  },
};

export class App {
  readonly store = new Store<AppState>({ ...DEFAULT_STATE });
  tg!: TriangleGroup;
  colouring: RotationColouring | null = null;
  pairs: number[][] | null = null;
  readonly painter: Painter;
  readonly view = new ViewController();
  measurement: Measurement | null = null;
  readonly overlay: OverlayRenderer;
  main!: Renderer;
  editor: Renderer | null = null;
  cb: AppCallbacks;

  // display
  camera: Camera = cameraFor('disk', 800, 600, 280);
  displayModel: ModelId = 'disk';
  morph = 0;
  private transition: { to: ModelId; phase: 'out' | 'in'; t: number } | null = null;
  quality = 1;
  private frameTimes: number[] = [];
  private uploadedMain = -1;
  private uploadedEditor = -1;
  private cpuMotifAt = 0;
  private cpuKey = '';
  private cpuLowRes = false;
  private sceneDirty = true;
  private editorDirty = true;
  private rafId = 0;
  private lastFrame = 0;
  private needs = true;
  ready = false;
  private coverageTimer = 0;
  private saveTimer = 0;
  cursor: Cursor | null = null;
  penDown = false;
  hintDone = false;
  private notices = new Map<string, NoticeSpec>();
  readonly stageEl: HTMLElement;
  readonly planeCanvas: HTMLCanvasElement;
  readonly editorCanvas: HTMLCanvasElement;
  reducedMotion = false;

  constructor(
    els: {
      stage: HTMLElement;
      plane: HTMLCanvasElement;
      overlay: HTMLCanvasElement;
      editor: HTMLCanvasElement;
    },
    cb: AppCallbacks,
  ) {
    this.cb = cb;
    this.stageEl = els.stage;
    this.planeCanvas = els.plane;
    this.editorCanvas = els.editor;
    this.overlay = new OverlayRenderer(els.overlay);
    this.painter = new Painter(emptyMotif());
    this.painter.onChange = () => this.onPaintChanged();
    this.view.onReanchor = (g) => this.measurement?.transform(g);
    this.hintDone = safeStorage.get(HINT_KEY) === '1';
    this.store.subscribe((s, prev) => this.onStateChanged(s, prev));
  }

  /* ------------------------------------------------------------------ startup */

  async start(forceCpu = false): Promise<void> {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.setReducedMotion(mq.matches);
    mq.addEventListener('change', (e) => this.setReducedMotion(e.matches));

    this.main = this.createRenderer(this.planeCanvas, forceCpu, true);
    this.editor = this.createRenderer(this.editorCanvas, this.main.kind === 'cpu', false);

    // Initial motif and settings: a shared link wins over the autosave, which wins over defaults.
    let motif: Motif = defaultMotif();
    const patch: Partial<AppState> = {};
    const hash = new URLSearchParams(location.hash.slice(1)).get('m');
    let loaded = false;
    if (hash) {
      try {
        const s = await decodeState(hash);
        Object.assign(patch, {
          p: s.p,
          q: s.q,
          r: s.r,
          rotation: s.rotation,
          scheme: s.scheme,
          model: s.model,
          grid: s.grid,
        });
        motif = s.motif;
        loaded = true;
        this.cb.onToast('Loaded the shared motif');
      } catch (err) {
        this.pushNotice({
          id: 'link',
          kind: 'error',
          html: `<p><strong>This link’s motif couldn’t be read</strong> (${escapeHtml(
            (err as Error).message,
          )}). Showing the example motif instead.</p>`,
          dismissible: true,
        });
      }
      history.replaceState(null, '', location.pathname + location.search);
    }
    if (!loaded) {
      const saved = safeStorage.get(STORAGE_KEY);
      if (saved) {
        try {
          const s = decodeBinary(fromBase64Url(saved));
          Object.assign(patch, {
            p: s.p,
            q: s.q,
            r: s.r,
            rotation: s.rotation,
            scheme: s.scheme,
            model: s.model,
            grid: s.grid,
          });
          motif = s.motif;
        } catch {
          /* ignore a corrupt autosave */
        }
      }
    }
    if (this.reducedMotion) patch.drift = false;
    this.store.set(patch);
    const st = this.store.get();
    this.displayModel = st.model;
    this.morph = st.model === 'disk' ? 0 : 1;
    this.view.drift = st.drift;
    this.painter.history.reset(motif);
    this.applyGroup(st.p, st.q, st.r);
    this.resize();
    const ro = new ResizeObserver(() => this.resize());
    ro.observe(this.stageEl);
    ro.observe(this.editorCanvas);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.requestRender();
    });
    this.requestRender();
  }

  private setReducedMotion(on: boolean): void {
    this.reducedMotion = on;
    this.view.reducedMotion = on;
    if (on && this.store.get().drift) this.store.set({ drift: false });
  }

  private createRenderer(canvas: HTMLCanvasElement, forceCpu: boolean, isMain: boolean): Renderer {
    if (!forceCpu) {
      try {
        const gl = new GLRenderer(canvas);
        canvas.addEventListener('webglcontextlost', (e) => {
          e.preventDefault();
          if (isMain) this.fallBackToCpu('The graphics context was lost');
        });
        return { kind: 'webgl', gl };
      } catch (err) {
        if (isMain) {
          this.pushNotice({
            id: 'cpu',
            kind: 'info',
            html: `<p><strong>WebGL2 isn’t available here</strong>, so this is a CPU preview at reduced resolution. Painting, travel, models and measuring all work, just more slowly.</p>`,
            dismissible: true,
          });
          console.warn('Falling back to the CPU renderer:', (err as Error).message);
        }
      }
    }
    if (forceCpu && isMain) {
      this.pushNotice({
        id: 'cpu',
        kind: 'info',
        html: '<p><strong>CPU preview</strong> (requested with <code>?renderer=cpu</code>): the same picture drawn without WebGL, at reduced resolution.</p>',
        dismissible: true,
      });
    }
    // A canvas that already has a WebGL context cannot give a 2D one; swap in a fresh element.
    let target = canvas;
    if (!forceCpu) {
      target = canvas.cloneNode(false) as HTMLCanvasElement;
      canvas.replaceWith(target);
      if (isMain) (this as { planeCanvas: HTMLCanvasElement }).planeCanvas = target;
      else (this as { editorCanvas: HTMLCanvasElement }).editorCanvas = target;
    }
    return { kind: 'cpu', cpu: new CPURenderer(target) };
  }

  private fallBackToCpu(reason: string): void {
    this.pushNotice({
      id: 'cpu',
      kind: 'error',
      html: `<p><strong>${escapeHtml(reason)}</strong> — switched to the CPU preview at reduced resolution.</p>`,
      actions: [{ label: 'Reload with WebGL', run: () => location.reload() }],
      dismissible: true,
    });
    const fresh = this.planeCanvas.cloneNode(false) as HTMLCanvasElement;
    this.planeCanvas.replaceWith(fresh);
    (this as { planeCanvas: HTMLCanvasElement }).planeCanvas = fresh;
    this.main = { kind: 'cpu', cpu: new CPURenderer(fresh) };
    this.sceneDirty = true;
    this.uploadedMain = -1;
    this.requestRender();
  }

  get rendererKind(): 'webgl' | 'cpu' {
    return this.main.kind;
  }

  /* ------------------------------------------------------------------ notices */

  pushNotice(n: NoticeSpec): void {
    this.notices.set(n.id, n);
    this.publishNotice();
  }

  dropNotice(id: string): void {
    if (this.notices.delete(id)) this.publishNotice();
  }

  private publishNotice(): void {
    // errors first, then info, then the blank-motif message
    const list = [...this.notices.values()];
    const pick =
      list.find((n) => n.kind === 'error') ?? list.find((n) => n.id !== 'blank') ?? list[0] ?? null;
    this.cb.onNotice(pick);
  }

  /* ------------------------------------------------------------------ state */

  private onStateChanged(s: AppState, prev: AppState): void {
    if (s.drift !== prev.drift) this.view.drift = s.drift;
    if (s.p !== prev.p || s.q !== prev.q || s.r !== prev.r) this.applyGroup(s.p, s.q, s.r);
    if (s.rotation !== prev.rotation || s.scheme !== prev.scheme || s.grid !== prev.grid) {
      this.sceneDirty = true;
      this.editorDirty = true;
      if (s.rotation !== prev.rotation) this.scheduleCoverage();
    }
    if (s.model !== prev.model) this.startModelTransition(s.model);
    if (s.tool !== prev.tool && isMeasureTool(s.tool)) {
      if (!this.measurement || this.measurement.kind !== s.tool) {
        this.measurement = new Measurement(s.tool as MeasureKind);
        this.cb.onReadout();
      }
    }
    if (s.tool !== prev.tool && this.penDown) this.setPen(false);
    if (
      s.p !== prev.p ||
      s.q !== prev.q ||
      s.r !== prev.r ||
      s.rotation !== prev.rotation ||
      s.scheme !== prev.scheme ||
      s.model !== prev.model ||
      s.grid !== prev.grid
    ) {
      this.scheduleSave();
    }
    this.cb.onState(s, prev);
    this.requestRender();
  }

  applyGroup(p: number, q: number, r: number): void {
    this.tg = buildTriangleGroup(p, q, r);
    this.colouring = findRotationColouring(p, q, r, { maxK: 8 });
    this.pairs = this.colouring ? pairPermutations(this.colouring) : null;
    this.view.resetAnchor(this.colouring?.k ?? 8);
    this.painter.setGroup(this.tg);
    this.view.reanchor(this.tg, this.pairs);
    this.sceneDirty = true;
    this.editorDirty = true;
    if (this.store.get().scheme === 'classes' && !this.colouring)
      this.store.set({ scheme: 'single' });
    this.cb.onGroupInfo();
    this.requestRender();
  }

  /** Validates a custom triple without applying it. */
  static checkTriple(p: number, q: number, r: number) {
    if (![p, q, r].every((n) => Number.isInteger(n) && n >= 2 && n <= 24))
      return { ok: false as const, reason: 'range' as const };
    const c = classifyTriangle(p, q, r);
    return c.kind === 'hyperbolic'
      ? { ok: true as const, cls: c }
      : { ok: false as const, reason: c.kind, cls: c };
  }

  /* ------------------------------------------------------------------ painting */

  private onPaintChanged(): void {
    this.editorDirty = true;
    this.cb.onHistory(this.painter.history.canUndo, this.painter.history.canRedo);
    this.scheduleCoverage();
    this.scheduleSave();
    this.requestRender();
  }

  private scheduleCoverage(): void {
    window.clearTimeout(this.coverageTimer);
    this.coverageTimer = window.setTimeout(() => {
      this.painter.measureCoverage(this.store.get().rotation);
      this.sceneDirty = true;
      this.editorDirty = true;
      const blank = this.painter.coverage[4] < 0.004;
      if (blank) {
        this.pushNotice({
          id: 'blank',
          kind: 'info',
          html: '<p><strong>Your tile is blank</strong> — pick a colour and paint.</p>',
          actions: [{ label: 'Restore the example', run: () => this.restoreExample() }],
        });
      } else this.dropNotice('blank');
      this.requestRender();
    }, 90);
  }

  private scheduleSave(): void {
    window.clearTimeout(this.saveTimer);
    this.saveTimer = window.setTimeout(() => {
      try {
        safeStorage.set(STORAGE_KEY, toBase64Url(encodeBinary(this.sharedState())));
      } catch {
        /* nothing to do */
      }
    }, 400);
  }

  sharedState() {
    const s = this.store.get();
    return {
      p: s.p,
      q: s.q,
      r: s.r,
      rotation: s.rotation,
      scheme: s.scheme,
      model: s.model,
      grid: s.grid,
      motif: this.painter.motif,
    };
  }

  async shareLink(): Promise<string> {
    const code = await encodeState(this.sharedState());
    return `${location.origin}${location.pathname}#m=${code}`;
  }

  restoreExample(): void {
    this.painter.replace(defaultMotif(), 'example');
    this.cb.onToast('Example motif restored — Undo brings your tile back');
  }

  clearMotif(): void {
    if (this.painter.motif.strokes.length === 0) return;
    this.painter.replace(emptyMotif(), 'clear');
    this.cb.onToast('Cleared — Undo brings it back');
  }

  undo(): void {
    const what = this.painter.undo();
    if (what) this.cb.onToast(what === 'stroke' ? 'Undid a stroke' : `Undid ${what}`);
  }

  redo(): void {
    const what = this.painter.redo();
    if (what) this.cb.onToast(what === 'stroke' ? 'Redid a stroke' : `Redid ${what}`);
  }

  strokeRole() {
    const s = this.store.get();
    return s.tool === 'eraser' ? ERASER : s.role;
  }

  brushSize(): number {
    return brushFromSlider(this.store.get().size);
  }

  /** Begins a stroke at a world point. */
  beginStroke(world: Complex, width = 1): void {
    const s = this.store.get();
    this.painter.begin(
      world,
      this.strokeRole(),
      this.brushSize(),
      s.rotation ? 'rotation' : 'reflection',
      width,
    );
    this.markHintDone();
    this.requestRender();
  }

  extendStroke(world: Complex, width = 1): void {
    this.painter.extend(world, width);
    this.requestRender();
  }

  endStroke(): void {
    this.painter.end();
  }

  /** Keyboard painting: the pen sits at the centre of the view and draws as you travel. */
  setPen(down: boolean): void {
    if (down === this.penDown) return;
    this.penDown = down;
    if (down) {
      this.stopDrift();
      this.beginStroke(this.view.centreWorld());
    } else {
      this.endStroke();
    }
    this.requestRender();
  }

  markHintDone(): void {
    if (this.hintDone) return;
    this.hintDone = true;
    safeStorage.set(HINT_KEY, '1');
    this.cb.onHintDone();
  }

  stopDrift(): void {
    if (this.store.get().drift) this.store.set({ drift: false });
  }

  /* ------------------------------------------------------------------ geometry queries */

  /** World point under a stage pixel (null outside the plane). */
  worldAt(x: number, y: number): Complex | null {
    const z = screenToDisk(this.camera, this.displayModel, x, y, this.morph);
    return z ? apply(inverse(this.view.view), z) : null;
  }

  diskAt(x: number, y: number): Complex | null {
    return screenToDisk(this.camera, this.displayModel, x, y, this.morph);
  }

  /** Is the world point inside the outlined (central) tile? */
  inCentralTile(world: Complex): boolean {
    return fold(this.tg, world, { maxIterations: 4000 }).m2Count === 0;
  }

  /** Brush radius in CSS px at a stage point (the brush is a hyperbolic disc). */
  brushRadiusAt(x: number, y: number): number {
    const z = this.diskAt(x, y);
    if (!z) return 0;
    // Euclidean disk radius of the brush near the domain is size·scale/2 at |w| ≈ 0; transport
    // it to z by the conformal factors and to the screen by the model's local scale.
    const hyper = this.brushSize() * domainScale(this.tg); // ≈ hyperbolic diameter
    const eps = 2;
    const z2 = this.diskAt(x + eps, y);
    if (!z2) return 0;
    const pxPerDisk = eps / Math.hypot(z2.re - z.re, z2.im - z.im);
    const euclid = (hyper / 2) * (1 - (z.re * z.re + z.im * z.im));
    return Math.max(1, euclid * pxPerDisk);
  }

  /* ------------------------------------------------------------------ models */

  private startModelTransition(to: ModelId): void {
    if (this.reducedMotion) {
      this.displayModel = to;
      this.morph = to === 'disk' ? 0 : 1;
      this.transition = null;
      this.updateCamera();
      this.requestRender();
      return;
    }
    if (this.displayModel === 'disk' || this.morph === 0) {
      this.displayModel = to;
      this.morph = 0;
      this.transition = to === 'disk' ? null : { to, phase: 'in', t: 0 };
    } else {
      this.transition = { to, phase: 'out', t: 0 };
    }
    this.updateCamera();
    this.requestRender();
  }

  get transitioning(): boolean {
    return this.transition !== null;
  }

  private tickTransition(dt: number): boolean {
    const tr = this.transition;
    if (!tr) return false;
    const DURATION = 0.55;
    tr.t = Math.min(1, tr.t + dt / DURATION);
    const e = tr.t < 0.5 ? 2 * tr.t * tr.t : 1 - Math.pow(-2 * tr.t + 2, 2) / 2;
    if (tr.phase === 'out') {
      this.morph = 1 - e;
      if (tr.t >= 1) {
        this.displayModel = tr.to;
        this.morph = 0;
        this.updateCamera();
        if (tr.to === 'disk') this.transition = null;
        else this.transition = { to: tr.to, phase: 'in', t: 0 };
      }
    } else {
      this.morph = e;
      if (tr.t >= 1) {
        this.morph = 1;
        this.transition = null;
      }
    }
    this.updateCamera();
    return true;
  }

  nextModel(): void {
    const ids = MODELS.map((m) => m.id);
    const i = ids.indexOf(this.store.get().model);
    this.store.set({ model: ids[(i + 1) % ids.length] });
  }

  /* ------------------------------------------------------------------ layout */

  diskRadiusPx = 280;

  resize(): void {
    const r = this.stageEl.getBoundingClientRect();
    const w = Math.max(1, r.width);
    const h = Math.max(1, r.height);
    const margin = Math.max(8, Math.min(30, 0.03 * Math.min(w, h)));
    this.diskRadiusPx = Math.max(40, Math.min(w, h) / 2 - margin);
    this.stageEl.style.setProperty('--disk-d', `${2 * this.diskRadiusPx}px`);
    this.updateCamera();
    this.overlay.resize(w, h, Math.min(window.devicePixelRatio || 1, 2));
    this.editorDirty = true;
    this.requestRender();
  }

  private updateCamera(): void {
    const r = this.stageEl.getBoundingClientRect();
    this.camera = cameraFor(
      this.displayModel,
      Math.max(1, r.width),
      Math.max(1, r.height),
      this.diskRadiusPx,
      this.morph,
    );
    // keyboard travel: the Cayley map turns directions at the centre by 90°
    this.view.keyFrame = this.displayModel === 'halfplane' ? -Math.PI / 2 : 0;
  }

  /* ------------------------------------------------------------------ scene + frames */

  private scene(forEditor: boolean): SceneSettings {
    const s = this.store.get();
    return {
      tg: this.tg,
      rotation: s.rotation,
      scheme: forEditor ? 'single' : s.scheme === 'parity' && s.rotation ? 'single' : s.scheme,
      colouring: this.colouring,
      grid: forEditor ? 'mirrors' : s.grid,
      box: this.painter.box,
      coverage: this.painter.coverage,
      palette: DEFAULT_PALETTE,
    };
  }

  frameParams(camera: Camera, pixelRatio: number, transparentBg = true): FrameParams {
    const s = this.store.get();
    // the social card (?og) shows the pattern alone
    const og = document.documentElement.classList.contains('og');
    const paintTool = !og && (s.tool === 'brush' || s.tool === 'eraser');
    return {
      camera,
      pixelRatio,
      model: this.displayModel,
      morph: this.morph,
      viewInverse: inverse(this.view.view),
      baseInverse: this.colouring
        ? invertPerm(this.view.anchorPerm.slice(0, this.colouring.k))
        : identityPerm(8),
      tileOutline: og ? 0 : paintTool ? 1 : 0.35,
      domainOutline: paintTool ? 0.32 : 0,
      focus: 0,
      background: transparentBg ? [0, 0, 0, 0] : [0x14 / 255, 0x1a / 255, 0x3a / 255, 1],
    };
  }

  private editorBox(): Box {
    const b = domainBox(this.tg, 0);
    const rot = this.store.get().rotation;
    const box = rot ? b : { x0: b.x0, y0: Math.min(0, b.y0 * 0.15), x1: b.x1, y1: b.y1 };
    const pw = (box.x1 - box.x0) * 0.06;
    const ph = (box.y1 - box.y0) * 0.06;
    return { x0: box.x0 - pw, y0: box.y0 - ph, x1: box.x1 + pw, y1: box.y1 + ph };
  }

  editorCamera(): Camera {
    const r = this.editorCanvas.getBoundingClientRect();
    return cameraForBox(this.editorBox(), Math.max(1, r.width), Math.max(1, r.height), 0.94);
  }

  private editorParams(): FrameParams {
    const cam = this.editorCamera();
    return {
      camera: cam,
      pixelRatio: Math.min(window.devicePixelRatio || 1, 2),
      model: 'disk',
      morph: 0,
      viewInverse: IDENTITY,
      baseInverse: identityPerm(8),
      tileOutline: 0,
      domainOutline: 2.2,
      focus: 1,
      background: [0, 0, 0, 0],
    };
  }

  /** Editor point → world (the editor shows the anchored frame with the identity view). */
  editorWorldAt(x: number, y: number): Complex {
    const cam = this.editorCamera();
    return {
      re: cam.cx + (x - cam.width / 2) * cam.scale,
      im: cam.cy + (cam.height / 2 - y) * cam.scale,
    };
  }

  /** Editor-space brush radius in CSS px. */
  editorBrushRadius(): number {
    const cam = this.editorCamera();
    return (this.brushSize() * domainScale(this.tg)) / 2 / cam.scale;
  }

  requestRender(): void {
    this.needs = true;
    if (this.rafId === 0 && document.visibilityState !== 'hidden') {
      this.rafId = requestAnimationFrame((t) => this.frame(t));
    }
  }

  private frame(now: number): void {
    this.rafId = 0;
    const dt = this.lastFrame ? Math.min(0.05, (now - this.lastFrame) / 1000) : 1 / 60;
    const continuous = this.view.animating || this.transition !== null;
    if (continuous && this.lastFrame) this.trackFrameTime(now - this.lastFrame);
    this.lastFrame = now;

    let changed = this.needs;
    this.needs = false;
    if (this.view.tick(dt)) changed = true;
    if (this.tickTransition(dt)) changed = true;
    if (changed) {
      this.view.reanchor(this.tg, this.pairs);
      if (this.penDown) this.extendStroke(this.view.centreWorld());
    }
    if (changed || this.painter.version !== this.uploadedMain) this.renderMain();
    const editorStepping = this.editor?.kind === 'cpu' && this.editor.cpu.busy;
    if (this.editorDirty || this.painter.version !== this.uploadedEditor || editorStepping) {
      this.renderEditor();
    }
    this.drawOverlay();

    if (!this.ready) {
      this.ready = true;
      document.documentElement.dataset.ready = 'true';
      document.getElementById('app')?.setAttribute('data-ready', 'true');
    }
    // once motion stops, replace the coarse CPU picture with a fine one
    if (
      this.main.kind === 'cpu' &&
      this.cpuLowRes &&
      !this.view.animating &&
      !this.transition &&
      !this.painter.painting
    ) {
      this.needs = true;
    }
    const cpuBusy = this.main.kind === 'cpu' && this.main.cpu.busy;
    const editorBusy = this.editor?.kind === 'cpu' && this.editor.cpu.busy;
    if (this.view.animating || this.transition || this.needs || cpuBusy || editorBusy) {
      if (this.rafId === 0 && document.visibilityState !== 'hidden') {
        this.rafId = requestAnimationFrame((t) => this.frame(t));
      }
    } else {
      this.lastFrame = 0;
    }
  }

  private trackFrameTime(ms: number): void {
    this.frameTimes.push(ms);
    if (this.frameTimes.length < 24) return;
    const avg = this.frameTimes.reduce((a, b) => a + b, 0) / this.frameTimes.length;
    this.frameTimes = [];
    if (avg > 26 && this.quality > 0.5) this.quality = Math.max(0.5, this.quality * 0.84);
    else if (avg < 17.5 && this.quality < 1) this.quality = Math.min(1, this.quality * 1.08);
  }

  pixelRatio(): number {
    return Math.min(window.devicePixelRatio || 1, 2) * this.quality;
  }

  renderMain(): void {
    const m = this.main;
    if (m.kind === 'webgl') {
      if (this.sceneDirty) {
        m.gl.setScene(this.scene(false));
        this.sceneDirty = false;
      }
      if (this.painter.version !== this.uploadedMain) {
        m.gl.uploadMotif(this.painter.canvas);
        this.uploadedMain = this.painter.version;
      }
      m.gl.render(this.frameParams(this.camera, this.pixelRatio()));
    } else {
      const now = performance.now();
      if (this.sceneDirty) {
        m.cpu.setScene(this.scene(false));
        this.sceneDirty = false;
      }
      if (
        this.painter.version !== this.uploadedMain &&
        (now - this.cpuMotifAt > 120 || !this.painter.painting)
      ) {
        m.cpu.setMotif(premultiply(this.painter.pixels()));
        this.uploadedMain = this.painter.version;
        this.cpuMotifAt = now;
      }
      // Progressive CPU rendering: a coarse picture while things move, a finer one once still.
      const moving = this.view.animating || this.transition !== null || this.painter.painting;
      const params = this.frameParams(this.camera, this.pixelRatio());
      const v = params.viewInverse;
      const c = params.camera;
      const key = [
        v.a.re,
        v.a.im,
        v.b.re,
        v.b.im,
        c.cx,
        c.cy,
        c.scale,
        c.width,
        c.height,
        params.model,
        params.morph,
        params.tileOutline,
        params.domainOutline,
        params.baseInverse.join(''),
        this.uploadedMain,
        moving,
      ].join('|');
      if (key !== this.cpuKey || !m.cpu.hasJob) {
        m.cpu.maxPixels = moving ? 45_000 : 170_000;
        m.cpu.start(params);
        this.cpuKey = key;
        this.cpuLowRes = moving;
      }
      m.cpu.step(moving ? 12 : 30);
      if (m.cpu.busy) this.needs = true;
    }
  }

  private renderEditor(): void {
    const e = this.editor;
    if (!e) return;
    const r = this.editorCanvas.getBoundingClientRect();
    if (r.width < 4 || r.height < 4) {
      // hidden (e.g. the settings sheet is closed): render later
      return;
    }
    const scene = this.scene(true);
    if (e.kind === 'webgl') {
      e.gl.setScene(scene);
      if (this.painter.version !== this.uploadedEditor) {
        e.gl.uploadMotif(this.painter.canvas);
        this.uploadedEditor = this.painter.version;
      }
      e.gl.render(this.editorParams());
      this.editorDirty = false;
    } else {
      if (this.editorDirty || this.painter.version !== this.uploadedEditor) {
        e.cpu.setScene(scene);
        e.cpu.setMotif(premultiply(this.painter.pixels()));
        this.uploadedEditor = this.painter.version;
        e.cpu.start(this.editorParams());
        this.editorDirty = false;
      }
      e.cpu.step(12);
    }
  }

  /** Forces a redraw of the editor (e.g. the settings sheet just opened). */
  refreshEditor(): void {
    this.editorDirty = true;
    this.requestRender();
  }

  drawOverlay(): void {
    const penAt = this.penDown ? { x: this.camera.width / 2, y: this.camera.height / 2 } : null;
    this.overlay.draw(
      {
        camera: this.camera,
        model: this.displayModel,
        view: this.view.view,
        hidden: this.transition !== null,
      },
      this.measurement,
      this.cursor,
      penAt,
    );
  }

  setCursor(c: Cursor | null): void {
    this.cursor = c;
    this.requestRender();
  }

  paintColor(): string {
    return PAINT[this.store.get().role].hex;
  }

  /* ------------------------------------------------------------------ measuring */

  addMeasurePoint(world: Complex): void {
    const tool = this.store.get().tool;
    if (!isMeasureTool(tool)) return;
    if (!this.measurement || this.measurement.kind !== tool)
      this.measurement = new Measurement(tool as MeasureKind);
    this.measurement.add(world);
    this.markHintDone();
    this.cb.onReadout();
    this.requestRender();
  }

  clearMeasurement(): void {
    const tool = this.store.get().tool;
    this.measurement = isMeasureTool(tool) ? new Measurement(tool as MeasureKind) : null;
    this.cb.onReadout();
    this.requestRender();
  }

  /** Index of the measurement handle within `radius` px of (x, y), or −1. */
  handleAt(x: number, y: number, radius = 16): number {
    const m = this.measurement;
    if (!m) return -1;
    const frame = {
      camera: this.camera,
      model: this.displayModel,
      view: this.view.view,
      hidden: false,
    };
    let best = -1;
    let bestD = radius;
    m.points.forEach((p, i) => {
      const s = OverlayRenderer.project(frame, p);
      if (!s) return;
      const d = Math.hypot(s.x - x, s.y - y);
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    });
    return best;
  }

  moveHandle(i: number, world: Complex): void {
    this.measurement?.move(i, world);
    this.cb.onReadout();
    this.requestRender();
  }

  /* ------------------------------------------------------------------ travel */

  goHome(): void {
    const back = this.view.home();
    this.measurement?.transform(back);
    this.view.reanchor(this.tg, this.pairs);
    this.requestRender();
  }

  recentreAt(x: number, y: number): void {
    const z = this.diskAt(x, y);
    if (!z) return;
    this.stopDrift();
    this.view.recentre(z);
    this.requestRender();
  }

  /* ------------------------------------------------------------------ export */

  async exportPng(
    size: number,
    transparent: boolean,
    onProgress: (f: number) => void,
  ): Promise<Blob> {
    const model = this.displayModel;
    const aspect = model === 'halfplane' ? 2 : model === 'band' ? 3 : 1;
    const width = size;
    const height = Math.round(size / aspect);
    // frame: disk/Klein fill the square; the half-plane shows [−2, 2]×[0, 2]; the band [−3, 3]×[−1, 1]
    const radius =
      model === 'halfplane' ? width / 4 : model === 'band' ? width / 6 : (width / 2) * 0.97;
    const camera: Camera = {
      cx: 0,
      cy: model === 'halfplane' ? 1 : 0,
      scale: 1 / radius,
      width,
      height,
    };
    const params = this.frameParams(camera, 1, transparent);
    params.tileOutline = 0;
    params.domainOutline = 0;
    let pixels: Uint8ClampedArray;
    if (this.main.kind === 'webgl') {
      const gl = this.main.gl;
      const hi = this.painter.renderHighRes(size >= 2048 ? 2048 : 1024);
      gl.uploadMotif(hi);
      gl.setScene({ ...this.scene(false), box: this.painter.box });
      try {
        pixels = await gl.renderImage(params, width, height, onProgress);
      } finally {
        gl.uploadMotif(this.painter.canvas);
        this.sceneDirty = true;
        this.requestRender();
      }
    } else {
      const off = document.createElement('canvas');
      const cpu = new CPURenderer(off);
      cpu.maxPixels = width * height;
      cpu.setScene(this.scene(false));
      cpu.setMotif(premultiply(this.painter.pixels()));
      cpu.start(params);
      while (!cpu.step(30)) {
        onProgress(0.9);
        await new Promise((r) => setTimeout(r, 0));
      }
      pixels = off.getContext('2d')!.getImageData(0, 0, width, height).data;
    }
    const out = document.createElement('canvas');
    out.width = width;
    out.height = height;
    const ctx = out.getContext('2d')!;
    ctx.putImageData(new ImageData(pixels as Uint8ClampedArray<ArrayBuffer>, width, height), 0, 0);
    // measurements, if any, drawn on top at export scale
    if (this.measurement && this.measurement.points.length > 0) {
      const ov = new OverlayRenderer(document.createElement('canvas'));
      ov.resize(width, height, 1);
      ov.draw({ camera, model, view: this.view.view, hidden: false }, this.measurement, null, null);
      ctx.drawImage(ov.canvas, 0, 0);
    }
    onProgress(1);
    return new Promise((resolve, reject) =>
      out.toBlob((b) => (b ? resolve(b) : reject(new Error('PNG encoding failed'))), 'image/png'),
    );
  }

  exportFilename(): string {
    const s = this.store.get();
    return `horocycle-${s.p}-${s.q}-${s.r}-${s.rotation ? 'rotations' : 'reflections'}-${this.displayModel}.png`;
  }

  /* ------------------------------------------------------------------ test hooks */

  /** Renders a frame now and reads the colours under the given stage points (CSS px). */
  samplePixels(points: { x: number; y: number }[]): [number, number, number, number][] {
    this.view.reanchor(this.tg, this.pairs);
    this.renderMain();
    const pr = this.main.kind === 'webgl' ? this.pixelRatio() : 0;
    if (this.main.kind === 'webgl') {
      const gl = this.main.gl;
      return points.map((p) => gl.readPixel(p.x * pr, p.y * pr));
    }
    const cpu = this.main.cpu;
    while (!cpu.step(50)) {
      /* finish the picture */
    }
    const c = cpu.canvas;
    const ctx = c.getContext('2d')!;
    const sx = c.width / this.camera.width;
    const sy = c.height / this.camera.height;
    return points.map((p) => {
      const d = ctx.getImageData(Math.floor(p.x * sx), Math.floor(p.y * sy), 1, 1).data;
      return [d[0], d[1], d[2], d[3]];
    });
  }

  /** Current view in a form tests can compare. */
  viewSnapshot() {
    const c = this.view.centreWorld();
    return {
      a: [this.view.view.a.re, this.view.view.a.im],
      b: [this.view.view.b.re, this.view.view.b.im],
      centre: [c.re, c.im],
      fromStart: this.view.distanceFromStart(),
      anchor: [
        this.view.anchor.a.re,
        this.view.anchor.a.im,
        this.view.anchor.b.re,
        this.view.anchor.b.im,
      ],
    };
  }
}

function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!,
  );
}
